// Écran « Remise grossiste » : comparer deux barèmes de remise arrière grossiste
// (et les accords génériqueurs) sur les VRAIS achats d'une officine.
//
// Le serveur ne rend que des agrégats par prix de boîte ; tout le reste se
// calcule ici, à chaque frappe, sans aller-retour. On peut donc tester un barème
// de négociation (« et si le seuil passait à 1 000 € ? ») en direct.
//
// La fiche PDF passe par fiche-impression.css, comme le presse-papier : aucune
// bibliothèque, « Partager → Imprimer » sur iPhone donne le PDF.

import {
  BAREMES_EXEMPLES, GENERIQUEURS_EXEMPLES, MODES, TAUX_RSF_BAS, ASSIETTES,
  problemesBareme, comparerBaremes, calculerGeneriqueurs, titulairesGeneriques, periodeParDefaut,
} from "../lib/remise-grossiste-logic.js";
import { eur } from "../lib/format.js";
import { decoderClaims } from "../lib/session.js";
import { jeton } from "../lib/etikid.js";
import "./fiche-impression.css";

// v2 (26/09) : un génériqueur porte { rsf, ra } et non plus un taux global. Une
// sauvegarde v1 est ignorée plutôt que mal relue.
// v3 (30/09, 2.15) : l'assiette par défaut devient « lignes sans remise » et les
// exemples Alliance passent à 3,8 % : un barème gardé en v2 n'a plus le même sens,
// il est laissé de côté. Les réglages génériqueurs, eux, sont repris.
const CLE_STOCKAGE = "bureau.remiseGrossiste.v3";
const CLE_V2 = "bureau.remiseGrossiste.v2";
// Affiché à la française (« 2,5 % », pas « 2.5 % »).
const TXT_BAS = String(TAUX_RSF_BAS).replace(".", ",");

const el = (tag, cls, texte) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (texte != null) n.textContent = texte;
  return n;
};
const copie = (o) => JSON.parse(JSON.stringify(o));
const nb = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);
const signe = (n) => (n > 0.5 ? "+" : "") + eur(n);

// ── État de l'écran ─────────────────────────────────────────────────────────
// Barèmes et génériqueurs sont gardés sur CE navigateur (confort seulement :
// perdus, on retombe sur les exemples). La période et le grossiste, eux, sont
// redemandés au serveur à chaque changement.
function etatInitial() {
  let garde = null;
  let ancien = null;
  try { garde = JSON.parse(localStorage.getItem(CLE_STOCKAGE) || "null"); } catch { garde = null; }
  if (!garde) { try { ancien = JSON.parse(localStorage.getItem(CLE_V2) || "null"); } catch { ancien = null; } }
  return {
    ...periodeParDefaut(),
    grossiste: "",
    actuel: garde?.actuel ?? copie(BAREMES_EXEMPLES.alliance),
    propose: garde?.propose ?? copie(BAREMES_EXEMPLES.cerp),
    generiqueurs: garde?.generiqueurs ?? ancien?.generiqueurs ?? copie(GENERIQUEURS_EXEMPLES),
    assiette: ASSIETTES[garde?.assiette] ? garde.assiette : "sans_remise",
    destinataire: "",
  };
}
export const ETAT = etatInitial();

function garder() {
  try {
    localStorage.setItem(CLE_STOCKAGE, JSON.stringify({
      actuel: ETAT.actuel, propose: ETAT.propose, generiqueurs: ETAT.generiqueurs, assiette: ETAT.assiette,
    }));
  } catch { /* stockage indisponible : l'écran marche quand même */ }
}

export async function chargerRemiseGrossiste(api) {
  return api("remise_grossiste_base", { debut: ETAT.debut, fin: ETAT.fin, grossiste: ETAT.grossiste || undefined });
}

// ── Éditeur de barème ───────────────────────────────────────────────────────
function editeurBareme(bareme, titre, surChange) {
  const bloc = el("div", "panel rg-bareme");
  const tete = el("div", "rg-tete");
  tete.appendChild(el("strong", null, titre));
  const presets = el("span", "rg-presets");
  for (const [cle, ex] of Object.entries(BAREMES_EXEMPLES)) {
    const b = el("button", "btn-ghost btn-sm", ex.nom.replace(/ \(.*\)/, ""));
    b.type = "button";
    b.title = "Remplacer par cet exemple";
    b.addEventListener("click", () => { Object.assign(bareme, copie(BAREMES_EXEMPLES[cle])); surChange(true); });
    presets.appendChild(b);
  }
  tete.appendChild(presets);
  bloc.appendChild(tete);

  const nom = el("input", "rg-nom");
  nom.value = bareme.nom || "";
  nom.placeholder = "Nom du barème";
  nom.addEventListener("input", () => { bareme.nom = nom.value; surChange(false); });
  bloc.appendChild(nom);

  const exclure = el("label", "rg-case");
  const cb = el("input");
  cb.type = "checkbox";
  cb.checked = bareme.exclureGeneriques !== false;
  cb.addEventListener("change", () => { bareme.exclureGeneriques = cb.checked; surChange(true); });
  exclure.append(cb, " Génériques exclus de la remise");
  if (ETAT.assiette === "hors_generiques") bloc.appendChild(exclure);
  else bloc.appendChild(el("div", "muted", "Assiette : les lignes sans remise sur facture (réglage en haut)."));

  // Chaque grossiste a SA définition des génériques ; celle de cet écran (le
  // référentiel) est la plus large. Part que ce grossiste remise quand même.
  const pg = el("label", "rg-case");
  const pgi = el("input", "rg-val");
  pgi.inputMode = "decimal";
  pgi.placeholder = "%";
  pgi.value = bareme.partGeneriques ?? "";
  pgi.disabled = bareme.exclureGeneriques === false;
  pgi.addEventListener("input", () => { bareme.partGeneriques = pgi.value; surChange(false); });
  pg.append("…dont remisés quand même : ", pgi, " % des génériques du référentiel");
  pg.title = "Alliance : 38 % mesuré aux Théâtres (sa liste de génériques est plus courte que le référentiel). Autre grossiste : lui demander par écrit.";
  if (ETAT.assiette === "hors_generiques") bloc.appendChild(pg);

  // Remise complémentaire versée à part (ristourne de coopérative, dividende,
  // prime de fin d'année), en % de toute l'assiette remisée.
  const div = el("label", "rg-case");
  const dv = el("input", "rg-val");
  dv.inputMode = "decimal";
  dv.placeholder = "%";
  dv.value = bareme.dividende ?? "";
  dv.addEventListener("input", () => { bareme.dividende = dv.value; surChange(false); });
  div.append("Dividende / remise complémentaire : ", dv, " % de l'assiette");
  bloc.appendChild(div);

  const tranches = el("div", "rg-tranches");
  bareme.tranches.forEach((tr, i) => {
    const derniere = i === bareme.tranches.length - 1;
    const ligne = el("div", "rg-tranche");
    ligne.appendChild(el("span", "muted", i === 0 ? "Boîte jusqu'à" : derniere ? "Au-delà" : "puis jusqu'à"));
    const max = el("input", "rg-max");
    max.inputMode = "decimal";
    max.placeholder = derniere ? "sans limite" : "€";
    max.disabled = derniere;
    max.value = derniere ? "" : (tr.max ?? "");
    max.addEventListener("input", () => { tr.max = max.value === "" ? null : max.value; surChange(false); });
    ligne.appendChild(max);

    const mode = el("select");
    for (const [v, lib] of Object.entries(MODES)) {
      const o = el("option", null, lib);
      o.value = v;
      o.selected = tr.mode === v;
      mode.appendChild(o);
    }
    mode.addEventListener("change", () => { tr.mode = mode.value; surChange(true); });
    ligne.appendChild(mode);

    if (tr.mode !== "rien") {
      const val = el("input", "rg-val");
      val.inputMode = "decimal";
      val.value = tr.valeur ?? "";
      val.placeholder = tr.mode === "pct" ? "%" : "€";
      val.addEventListener("input", () => { tr.valeur = val.value; surChange(false); });
      ligne.appendChild(val);
    }
    if (bareme.tranches.length > 1) {
      const suppr = el("button", "btn-ghost btn-sm", "×");
      suppr.type = "button";
      suppr.title = "Retirer cette tranche";
      suppr.addEventListener("click", () => {
        bareme.tranches.splice(i, 1);
        bareme.tranches[bareme.tranches.length - 1].max = null;
        surChange(true);
      });
      ligne.appendChild(suppr);
    }
    tranches.appendChild(ligne);
  });
  bloc.appendChild(tranches);

  const ajout = el("button", "btn-ghost btn-sm", "+ tranche");
  ajout.type = "button";
  ajout.addEventListener("click", () => {
    // La nouvelle tranche s'insère AVANT la dernière (« au-delà ») : c'est elle
    // qui reçoit une limite, la dernière reste sans borne.
    const avant = bareme.tranches[bareme.tranches.length - 2];
    const base = avant && avant.max ? Number(avant.max) * 2 : 1000;
    bareme.tranches.splice(bareme.tranches.length - 1, 0, { max: base, mode: "pct", valeur: 0 });
    surChange(true);
  });
  bloc.appendChild(ajout);

  const pb = problemesBareme(bareme);
  if (pb.length) bloc.appendChild(el("div", "err", pb.join(" ")));
  return bloc;
}

// ── Résultats ───────────────────────────────────────────────────────────────
function tableau(entetes, lignes, pied) {
  const card = el("div", "card");
  const t = el("table");
  const th = el("tr");
  entetes.forEach(([lib, num]) => th.appendChild(el("th", num ? "num" : null, lib)));
  t.appendChild(el("thead")).appendChild(th);
  const tb = el("tbody");
  for (const cellules of lignes) {
    const tr = el("tr");
    cellules.forEach((c, i) => tr.appendChild(el("td", entetes[i][1] ? "num" : null, c)));
    tb.appendChild(tr);
  }
  if (pied) {
    const tr = el("tr", "rg-total");
    pied.forEach((c, i) => tr.appendChild(el("td", entetes[i][1] ? "num" : null, c)));
    tb.appendChild(tr);
  }
  t.appendChild(tb);
  card.appendChild(t);
  return card;
}

// Mesuré le 30/09/2026 aux Théâtres (12 mois) : assiette « hors génériques » selon
// Alliance 1 346 263 € contre 1 258 100 € ici. Alliance ne retire que la ligne
// « génériques » de ses MDL, plus étroite que le référentiel BDPM utilisé ici.
const AVERT_ASSIETTE = "Chaque grossiste a sa propre liste de génériques. Cet écran retire ceux du référentiel officiel, la liste la plus large ; la ligne « génériques remisés quand même » rajoute la part que chaque grossiste remise malgré tout. Pour Alliance, 38 % (mesuré aux Théâtres : son assiette hors génériques est environ 7 % plus large que celle de cet écran). C'est une moyenne, pas une liste produit par produit. Pour tout autre grossiste, la part est à lui demander par écrit ; à défaut, laisser 0.";

const AVERT_SANS_REMISE = "Assiette « lignes sans remise » : une ligne déjà remisée sur la facture (remise laboratoire, partenariat) sort de l'assiette, un générique non remisé y entre. C'est la règle constatée chez Alliance le 30/09/2026 : 3,8 % des achats sans remise du mois précédent, mesuré à 3,79 % sur 18 mois pleins. Le barème proposé est supposé suivre la même règle : à faire confirmer par écrit par le grossiste.";

const aGeneriques = (comp) => comp.generiques && (comp.generiques.actuel > 0.5 || comp.generiques.propose > 0.5);
const pctG = (p) => `${Math.round(p * 100)} %`;
const aDividende = (comp) => comp.dividende && (comp.dividende.actuel > 0.5 || comp.dividende.propose > 0.5);

// Ce qu'on a appris le 30/09/2026 en lisant les 326 documents Alliance des
// Théâtres et les 90 de Cardinet, puis les relevés bancaires : le taux PROMIS
// n'est pas le montant VERSÉ, et le vrai chiffre n'est pas là où on le cherche.
// Rapport : ~/livrables/2026-09-30/theatres-remises-alliance/rapport.md.
const PROMESSE_VERITE = [
  "Un taux promis n'est pas un rendement. Chez Alliance, le taux est bien tenu les bons mois : 3,8 % des achats sans remise sur facture du mois précédent (ou, ce qui revient au même en euros, 3,6 % hors génériques). Mais sur 12 mois les relevés n'en versent que 3,26 % (Théâtres) : certains mois sont payés moins, deux mois rien du tout.",
  "Les manques sont rattrapés en partie par des régularisations versées en banque, hors relevés (virements « REM COMM », libellé = fin de la période couverte). Une régularisation ne compte que pour les mois qu'elle couvre : celle de juin 2026 couvre aussi mars → août 2025. Rattachée à sa période : 3,54 % des achats sans remise aux Théâtres ; rapporté à TOUS les achats nets, Théâtres et Cardinet ont reçu ≈ 3,0 %.",
  "Rien de tout cela n'est écrit : ni décompte des régularisations, ni contrat qui fixe le taux. Le versement peut changer ou s'arrêter du jour au lendemain. La transparence et la sécurité du versement comptent autant que le taux.",
  "Où lire la remise arrière Alliance : la ligne « Remises Commerciales/abn marge », dans le bloc « Avantages commerciaux – récapitulatif du mois », en dernière page du relevé de la DERNIÈRE décade du mois. Elle n'est ni sur les factures, ni sur les deux autres relevés, ni sur HubPharma.",
  "Ne pas additionner les « remises partenariats » : ce sont les remises des laboratoires, déjà déduites ligne à ligne sur les factures. Les « avoirs commerciaux » sont des retours de produits, pas une remise.",
  "Piège d'assiette : la « facturation nette » d'Alliance (relevés, analyse TVA) est DÉJÀ diminuée de la remise arrière. Rapporter la remise à ce chiffre, c'est la rapporter à une base qu'elle a elle-même réduite.",
  "La remise arrière du mois M est calculée sur les achats du mois M-1. Un mois bas en achats donne une remise haute le mois suivant, et inversement : ne jamais juger sur un seul mois.",
  "Même prudence pour l'offre concurrente : un barème proposé (4,75 %, 20 €/boîte) est une promesse. Demander au grossiste où et quand il le verse, et le vérifier sur ses propres documents après 3 mois (ou sur les relevés d'une pharmacie déjà cliente).",
];

function hypotheses(donnees) {
  const m = donnees.meta || {};
  return [
    `Achats du grossiste « ${donnees.grossiste} » du ${fr(donnees.debut)} au ${fr(donnees.fin)}` +
      (m.premiere ? ` (factures présentes du ${fr(m.premiere)} au ${fr(m.derniere)})` : "") + ".",
    "Le seuil de chaque tranche s'applique au prix HT NET facturé par boîte ; une boîte au prix exact du seuil est dans la tranche du dessous.",
    "Générique = générique ou substituable au répertoire des génériques (BDPM). Princeps, parapharmacie, LPP et médicaments chers hors répertoire sont dans l'assiette remisée.",
    `Génériqueur, sur le prix brut (PFHT) facturé : remise sur facture garantie sur tout le brut sauf la part à ${TXT_BAS} % sur facture (qui garde son taux), plus remise arrière sur tout le brut. Toutes les boîtes du titulaire sont supposées couvertes par l'accord, et les remises versées directement à l'officine.`,
    ETAT.assiette === "sans_remise" ? AVERT_SANS_REMISE : AVERT_ASSIETTE,
    "Le dividende ou la remise complémentaire est un pourcentage de toute l'assiette remisée (génériques exclus si l'un des barèmes les exclut), ajouté au total, hors tranches.",
    m.doublons_ecartes ? `${m.doublons_ecartes} facture(s) reçues par deux canaux n'ont été comptées qu'une fois.` : null,
    m.lignes_sans_prix ? `${m.lignes_sans_prix} ligne(s) sans prix ni quantité ignorées.` : null,
  ].filter(Boolean);
}

function fr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || "");
}

// L'assiette « sans remise » a besoin du champ `remisee` des agrégats (serveur
// 2.15). Sans lui, toutes les lignes passeraient pour non remisées : on revient à
// l'assiette « hors génériques » et on le dit, plutôt que de calculer faux.
function assietteEffective(lignes) {
  if (ETAT.assiette !== "sans_remise") return { assiette: ETAT.assiette, repli: false };
  const ok = lignes.some((l) => typeof l.remisee === "boolean");
  return ok ? { assiette: "sans_remise", repli: false } : { assiette: "hors_generiques", repli: true };
}

function calculer(donnees) {
  const lignes = donnees.agregats || [];
  const { assiette, repli } = assietteEffective(lignes);
  return {
    repli,
    comp: comparerBaremes(lignes, ETAT.actuel, ETAT.propose, { assiette }),
    gens: calculerGeneriqueurs(lignes, ETAT.generiqueurs),
    titulaires: titulairesGeneriques(lignes),
  };
}

function rendreResultats(zone, donnees) {
  const pbs = [...problemesBareme(ETAT.actuel), ...problemesBareme(ETAT.propose)];
  if (pbs.length) {
    // Le message est répété ici : pendant la frappe, l'éditeur n'est pas
    // redessiné (sinon le champ perdrait le focus), donc il ne l'affiche pas.
    zone.replaceChildren(el("div", "err", "Calcul suspendu : " + pbs.join(" ")));
    return;
  }
  const { comp, gens, repli } = calculer(donnees);
  const frag = document.createDocumentFragment();
  const nA = ETAT.actuel.nom || "Actuel";
  const nP = ETAT.propose.nom || "Proposé";

  frag.appendChild(el("h2", "sect", "Remise grossiste, tranche par tranche"));
  frag.appendChild(tableau(
    [["Prix de la boîte"], ["Achats HT", 1], ["Boîtes", 1], [nA, 1], [nP, 1], ["Écart", 1]],
    comp.segments.map((s) => [s.libelle, eur(s.ht), nb(s.boites), eur(s.actuel), eur(s.propose), signe(s.ecart)])
      .concat(aGeneriques(comp) ? [[`Génériques remisés quand même (${pctG(comp.generiques.partActuel)} / ${pctG(comp.generiques.partPropose)} de ${eur(comp.generiques.ht)})`, "", "", eur(comp.generiques.actuel), eur(comp.generiques.propose), signe(comp.generiques.propose - comp.generiques.actuel)]] : [])
      .concat(aDividende(comp) ? [["Dividende / remise complémentaire", "", "", eur(comp.dividende.actuel), eur(comp.dividende.propose), signe(comp.dividende.propose - comp.dividende.actuel)]] : []),
    ["Total", eur(comp.total.ht), nb(comp.total.boites), eur(comp.total.actuel), eur(comp.total.propose), signe(comp.total.ecart)],
  ));
  if (repli) frag.appendChild(el("div", "err", "Le serveur ne dit pas encore quelles lignes sont remisées : calcul fait sur l'assiette « tout sauf les génériques ». Rechargez la page dans quelques minutes."));
  if (comp.exclusHt) frag.appendChild(el("p", "muted", comp.assiette === "sans_remise"
    ? `${eur(comp.exclusHt)} HT de lignes déjà remisées sur facture, hors assiette (les génériques remisés sont traités par le génériqueur, ci-dessous).`
    : `${eur(comp.exclusHt)} HT de génériques hors assiette (traités par le génériqueur, ci-dessous).`));
  frag.appendChild(el("div", "panel muted", "⚠️ " + (comp.assiette === "sans_remise" ? AVERT_SANS_REMISE : AVERT_ASSIETTE)));

  frag.appendChild(el("h2", "sect", "Génériqueurs"));
  frag.appendChild(editeurGeneriqueurs(donnees, gens));

  const connus = gens.filter((g) => g.gain != null);
  const inconnus = gens.filter((g) => g.gain == null && g.titulaire);
  const totalGen = connus.reduce((t, g) => t + g.gain, 0);
  const synth = el("div", "panel rg-synthese");
  synth.appendChild(el("div", "rg-ligne-synth", `Remise grossiste : ${signe(comp.total.ecart)} par an`));
  synth.appendChild(el("div", "rg-ligne-synth", `Génériqueurs chiffrés : ${signe(totalGen)} par an`));
  synth.appendChild(el("strong", "rg-ligne-synth", `Solde : ${signe(comp.total.ecart + totalGen)} par an`));
  if (inconnus.length) synth.appendChild(el("div", "muted", `Non chiffré faute de taux : ${inconnus.map((g) => g.titulaire).join(", ")}.`));
  synth.appendChild(el("div", "muted", "Hors cotisations de groupement, qui se comparent à part."));
  frag.appendChild(synth);

  frag.appendChild(el("h2", "sect", "Hypothèses du calcul"));
  const ul = el("ul", "rg-hyp");
  hypotheses(donnees).forEach((h) => ul.appendChild(el("li", null, h)));
  frag.appendChild(ul);

  const pv = el("details", "panel rg-verite");
  pv.appendChild(el("summary", null, "Promesse et vérité : comment trouver le vrai chiffre d'un grossiste"));
  const ulv = el("ul", "rg-hyp");
  PROMESSE_VERITE.forEach((h) => ulv.appendChild(el("li", null, h)));
  pv.appendChild(ulv);
  frag.appendChild(pv);

  zone.replaceChildren(frag);
}

function editeurGeneriqueurs(donnees, gens) {
  const wrap = el("div");
  const card = el("div", "card");
  const t = el("table");
  const th = el("tr");
  [["Titulaire"], ["Remise facture garantie %<br>actuel · proposé"], ["Remise arrière %<br>actuel · proposé"],
    ["Achats bruts", 1], [`dont part ${TXT_BAS} %`, 1], ["Remises / an actuel", 1], ["Remises / an proposé", 1], ["Gain / an", 1], [""]]
    .forEach(([lib, n]) => {
      const c = el("th", n ? "num" : null);
      lib.split("<br>").forEach((morceau, k) => { if (k) c.appendChild(el("br")); c.appendChild(document.createTextNode(morceau)); });
      th.appendChild(c);
    });
  t.appendChild(el("thead")).appendChild(th);
  const tb = el("tbody");
  ETAT.generiqueurs.forEach((g, i) => {
    const r = gens[i];
    const tr = el("tr");
    const saisie = (valeur, surValeur, numerique = true) => {
      const inp = el("input", numerique ? "rg-taux" : "rg-gen");
      inp.value = valeur ?? "";
      if (numerique) inp.inputMode = "decimal";
      inp.addEventListener("change", () => { surValeur(inp.value === "" ? null : inp.value); garder(); rafraichir(); });
      return inp;
    };
    // Les deux taux d'un accord : « actuel · proposé » côte à côte, c'est le
    // curseur que Mehdi déplace d'une officine à l'autre.
    const paire = (cle) => {
      g[cle] ??= { actuel: null, propose: null };
      const td = el("td");
      td.append(
        saisie(g[cle].actuel, (v) => { g[cle].actuel = v; }),
        " ",
        saisie(g[cle].propose, (v) => { g[cle].propose = v; }),
      );
      return td;
    };
    const tdTit = el("td");
    tdTit.appendChild(saisie(g.titulaire, (v) => { g.titulaire = v; }, false));
    tr.appendChild(tdTit);
    tr.appendChild(paire("rsf"));
    tr.appendChild(paire("ra"));
    tr.appendChild(el("td", "num", eur(r.baseBrut)));
    tr.appendChild(el("td", "num", eur(r.brutBas)));
    tr.appendChild(el("td", "num", r.actuel == null ? "taux inconnu" : eur(r.actuel)));
    tr.appendChild(el("td", "num", r.propose == null ? "taux inconnu" : eur(r.propose)));
    tr.appendChild(el("td", "num", r.gain == null ? "—" : signe(r.gain)));
    const td = el("td");
    const x = el("button", "btn-ghost btn-sm", "×");
    x.type = "button";
    x.addEventListener("click", () => { ETAT.generiqueurs.splice(i, 1); garder(); rafraichir(); });
    td.appendChild(x);
    tr.appendChild(td);
    tb.appendChild(tr);
  });
  t.appendChild(tb);
  card.appendChild(t);
  wrap.appendChild(card);

  const presents = new Set(ETAT.generiqueurs.map((g) => String(g.titulaire || "").toUpperCase()));
  const absents = calculer(donnees).titulaires.filter((t) => ![...presents].some((p) => p && t.titulaire.toUpperCase().includes(p)));
  if (absents.length) {
    const sugg = el("div", "rg-presets");
    sugg.appendChild(el("span", "muted", "Ajouter : "));
    for (const a of absents.slice(0, 5)) {
      const b = el("button", "btn-ghost btn-sm", `${a.titulaire} (${eur(a.baseBrut)})`);
      b.type = "button";
      b.addEventListener("click", () => {
        ETAT.generiqueurs.push({ titulaire: a.titulaire, rsf: { actuel: null, propose: null }, ra: { actuel: null, propose: null } });
        garder(); rafraichir();
      });
      sugg.appendChild(b);
    }
    wrap.appendChild(sugg);
  }
  return wrap;
}

// ── Fiche PDF ───────────────────────────────────────────────────────────────
function imprimerFiche(donnees) {
  const { comp, gens } = calculer(donnees);
  const officine = decoderClaims(jeton()).tenantNom || "";
  const f = el("div", "fiche");
  const tete = el("header", "fiche-entete");
  if (officine) tete.appendChild(el("p", "fiche-officine", officine));
  tete.appendChild(el("h1", "fiche-titre", "Remise arrière grossiste — comparaison"));
  tete.appendChild(el("p", "fiche-sous-titre", [
    `${ETAT.actuel.nom || "Actuel"} face à ${ETAT.propose.nom || "Proposé"}`,
    ETAT.destinataire ? `pour ${ETAT.destinataire}` : null,
    new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }),
  ].filter(Boolean).join(" · ")));
  f.appendChild(tete);

  const section = (titre, entrees) => {
    const s = el("section", "fiche-section");
    s.appendChild(el("h2", "fiche-section-titre", titre));
    for (const [t, v, d] of entrees) {
      const a = el("article", "fiche-entree");
      a.appendChild(el("p", "fiche-entree-titre", t));
      a.appendChild(el("p", "fiche-valeur", v));
      if (d) a.appendChild(el("p", "fiche-detail", d));
      s.appendChild(a);
    }
    f.appendChild(s);
  };
  const decrire = (b) => b.tranches.map((tr, i) => {
    const bas = i === 0 ? null : b.tranches[i - 1].max;
    const lib = tr.max == null ? (bas == null ? "toutes boîtes" : `au-delà de ${bas} €`) : `jusqu'à ${tr.max} €`;
    const val = tr.mode === "pct" ? `${tr.valeur} %` : tr.mode === "boite" ? `${tr.valeur} €/boîte` : "0";
    return `${lib} : ${val}`;
  }).join(" · ") + (Number(String(b.dividende ?? "").replace(",", ".")) > 0 ? ` · + dividende ${b.dividende} % de l'assiette` : "")
    + (b.exclureGeneriques !== false && Number(String(b.partGeneriques ?? "").replace(",", ".")) > 0 ? ` · génériques remisés quand même : ${b.partGeneriques} %` : "");

  section("Barèmes comparés", [
    [ETAT.actuel.nom || "Actuel", decrire(ETAT.actuel), null],
    [ETAT.propose.nom || "Proposé", decrire(ETAT.propose), null],
  ]);
  section("Remise grossiste par tranche de prix", comp.segments.map((s) => [
    s.libelle, signe(s.ecart),
    `${eur(s.ht)} HT · ${nb(s.boites)} boîtes · ${eur(s.actuel)} → ${eur(s.propose)}`,
  ]).concat(aGeneriques(comp) ? [["Génériques remisés quand même", signe(comp.generiques.propose - comp.generiques.actuel),
    `${pctG(comp.generiques.partActuel)} → ${pctG(comp.generiques.partPropose)} de ${eur(comp.generiques.ht)} · ${eur(comp.generiques.actuel)} → ${eur(comp.generiques.propose)}`]] : [])
    .concat(aDividende(comp) ? [["Dividende / remise complémentaire", signe(comp.dividende.propose - comp.dividende.actuel),
    `${eur(comp.dividende.actuel)} → ${eur(comp.dividende.propose)}`]] : []).concat([["Total", signe(comp.total.ecart), `${eur(comp.total.actuel)} → ${eur(comp.total.propose)} sur ${eur(comp.total.ht)} HT`]]));
  section("Génériqueurs", gens.filter((g) => g.titulaire).map((g, i) => {
    const conf = ETAT.generiqueurs[i] || {};
    const taux = (c) => `${c?.actuel ?? "?"} % → ${c?.propose ?? "?"} %`;
    return [
      g.titulaire, g.gain == null ? "taux inconnu" : signe(g.gain),
      `${eur(g.baseBrut)} bruts (dont ${eur(g.brutBas)} à ${TXT_BAS} % sur facture) · ${nb(g.boites)} boîtes · ` +
      `remise facture garantie ${taux(conf.rsf)} · remise arrière ${taux(conf.ra)}` +
      (g.gain == null ? "" : ` · ${eur(g.actuel)} → ${eur(g.propose)} par an`),
    ];
  }));
  section("Hypothèses", hypotheses(donnees).map((h, i) => [`${i + 1}.`, "", h]));
  f.appendChild(el("footer", "fiche-pied", "Calcul Dans le Bureau · hors cotisations de groupement"));

  const app = document.getElementById("app");
  app.appendChild(f);
  document.body.classList.add("mode-impression");
  setTimeout(() => {
    window.print();
    setTimeout(() => { document.body.classList.remove("mode-impression"); f.remove(); }, 400);
  }, 60);
}

// ── Montage ─────────────────────────────────────────────────────────────────
let rafraichir = () => {};

/**
 * `recharger()` redemande les agrégats (période ou grossiste changés) ;
 * tout le reste se recalcule sur place.
 */
export function rendreRemiseGrossiste(racine, donnees, recharger) {
  const frag = document.createDocumentFragment();
  frag.appendChild(el("p", "muted",
    "Compare deux barèmes de remise arrière sur vos achats réels chez un grossiste, plus l'effet des accords génériqueurs. Modifiez un barème : le calcul suit."));

  const ctl = el("div", "panel rg-controles");
  const champ = (lib, input) => { const c = el("label", "champ"); c.append(el("span", "muted", lib), input); return c; };
  const d1 = el("input"); d1.type = "date"; d1.value = ETAT.debut;
  const d2 = el("input"); d2.type = "date"; d2.value = ETAT.fin;
  const sel = el("select");
  for (const c of donnees.candidats || []) {
    const o = el("option", null, `${c.libelle} — ${nb(c.lignes)} lignes`);
    o.value = c.cle;
    o.selected = c.cle === donnees.grossiste;
    sel.appendChild(o);
  }
  const go = el("button", "btn btn-sm", "Recalculer");
  go.type = "button";
  go.addEventListener("click", () => { ETAT.debut = d1.value; ETAT.fin = d2.value; ETAT.grossiste = sel.value; recharger(); });
  const dest = el("input"); dest.placeholder = "ex. Dr Martin"; dest.value = ETAT.destinataire;
  dest.addEventListener("input", () => { ETAT.destinataire = dest.value; });
  const pdf = el("button", "btn-ghost btn-sm", "Fiche PDF");
  pdf.type = "button";
  pdf.addEventListener("click", () => imprimerFiche(donnees));
  const ass = el("select");
  for (const [v, lib] of Object.entries(ASSIETTES)) {
    const o = el("option", null, lib);
    o.value = v;
    o.selected = ETAT.assiette === v;
    ass.appendChild(o);
  }
  ass.addEventListener("change", () => { ETAT.assiette = ass.value; garder(); peindreBaremes(); rendreResultats(zone, donnees); });
  ctl.append(champ("Du", d1), champ("Au", d2), champ("Grossiste", sel), go, champ("Assiette", ass), champ("Fiche pour", dest), pdf);
  frag.appendChild(ctl);

  const baremes = el("div", "rg-baremes");
  const zone = el("div");
  const peindreBaremes = () => {
    baremes.replaceChildren(
      editeurBareme(ETAT.actuel, "Barème actuel", surChange),
      editeurBareme(ETAT.propose, "Barème proposé", surChange),
    );
  };
  // `structure` = une tranche ajoutée/retirée ou un type changé : l'éditeur doit
  // être redessiné. Sinon (frappe dans un champ) on ne touche PAS à l'éditeur,
  // ce qui ferait perdre le focus à chaque chiffre tapé.
  function surChange(structure) {
    garder();
    if (structure) peindreBaremes();
    rendreResultats(zone, donnees);
  }
  rafraichir = () => rendreResultats(zone, donnees);
  peindreBaremes();
  frag.appendChild(baremes);

  if (!(donnees.agregats || []).length) {
    zone.appendChild(el("div", "muted", "Aucune ligne d'achat pour ce grossiste sur cette période."));
  } else {
    rendreResultats(zone, donnees);
  }
  frag.appendChild(zone);
  racine.replaceChildren(frag);
}
